/**
 * **Riwayat perubahan satu baris master** — *"siapa mengubah ini, kapan, dari apa jadi apa"*.
 *
 * ### ⛔⛔ Kenapa ini akhirnya dibuat
 * `master_event` sudah **append-only sejak awal** (Guardrail #5) dan mencatat setiap perubahan
 * lengkap dengan pelaku serta nilai sebelum/sesudah. Endpoint-nya pun sudah ada
 * (`GET /master/:path/:id/history`). Yang tak pernah ada: tempat membacanya.
 *
 * ⇒ Jejak yang tak bisa dibaca hanya membebani disk — ia tak pernah menjawab pertanyaan yang
 *   membuatnya ditulis. Dan pertanyaannya nyata: *"kenapa harga ini berubah?"*, *"siapa yang
 *   mematikan promo itu?"* — pertanyaan yang muncul saat margin bulan ini meleset, berminggu
 *   sesudah perbuatannya.
 *
 * ### ⭐ Yang ditampilkan: BEDANYA, bukan seluruh isi baris
 * Menampilkan `after_json` utuh membuat orang membandingkan dua blok JSON dengan mata. Yang
 * berguna cuma kolom yang **berubah** — dan itu bisa dihitung.
 */
import { Empty, Table, Tag, Typography } from 'antd';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { api } from '../api/client';
import type { MasterEvent } from '../api/client';

/** Kolom yang berubah, beserta nilai lama → baru. */
function beda(e: MasterEvent): { kolom: string; dari: unknown; jadi: unknown }[] {
  const b = e.before_json ?? {};
  const a = e.after_json ?? {};
  const kunci = new Set([...Object.keys(b), ...Object.keys(a)]);
  const out: { kolom: string; dari: unknown; jadi: unknown }[] = [];
  for (const k of kunci) {
    /* ⚠️ `updated_at` selalu berubah dan tak pernah menarik — ia cuma membuat daftar bedanya ramai. */
    if (k === 'updated_at' || k === 'created_at') continue;
    if (JSON.stringify(b[k]) !== JSON.stringify(a[k])) out.push({ kolom: k, dari: b[k], jadi: a[k] });
  }
  return out;
}

function tampil(v: unknown): string {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

export function MasterHistory({ path, id }: { readonly path: string; readonly id: string }) {
  const { t } = useTranslation();
  const [rows, setRows] = useState<readonly MasterEvent[] | null>(null);

  useEffect(() => {
    let alive = true;
    void api
      .history(path, id)
      .then((r) => { if (alive) setRows(r); })
      /* ⚠️ Gagal memuat riwayat TIDAK boleh menutup laci suntingnya — yang utama tetap menyunting. */
      .catch(() => { if (alive) setRows([]); });
    return () => { alive = false; };
  }, [path, id]);

  if (rows === null) return null;
  if (rows.length === 0) {
    return <Empty description={t('history.empty')} data-testid="riwayat-kosong" />;
  }

  return (
    <Table<MasterEvent>
      size="small"
      rowKey="id"
      pagination={false}
      dataSource={[...rows]}
      data-testid="riwayat"
      columns={[
        {
          title: t('history.when'),
          width: 170,
          render: (_: unknown, e: MasterEvent) => e.created_at.slice(0, 19).replace('T', ' '),
        },
        {
          title: t('history.action'),
          width: 110,
          render: (_: unknown, e: MasterEvent) => <Tag>{e.action}</Tag>,
        },
        {
          /* ⭐ Pelaku diambil dari SNAPSHOT, bukan join ke staf: nama yang berubah kelak tak boleh
             mengubah jejak lama — jejak yang bisa berubah bukan jejak (P5). */
          title: t('history.actor'),
          width: 160,
          render: (_: unknown, e: MasterEvent) =>
            e.actor_name_snapshot ?? e.actor_code_snapshot ?? e.actor_kind,
        },
        {
          title: t('history.changed'),
          render: (_: unknown, e: MasterEvent) => {
            const d = beda(e);
            if (d.length === 0) return <Typography.Text type="secondary">—</Typography.Text>;
            return (
              <ul style={{ margin: 0, paddingInlineStart: 18 }}>
                {d.map((x) => (
                  <li key={x.kolom}>
                    <b>{x.kolom}</b>: {tampil(x.dari)} → {tampil(x.jadi)}
                  </li>
                ))}
              </ul>
            );
          },
        },
      ]}
    />
  );
}
